import { LoaderCircle } from 'lucide-react';
import { Progress as ProgressPrimitive } from 'radix-ui';
import type { ComponentProps } from 'react';
import { t } from '@ticarishift/i18n';
import { cn } from '../lib/cn.ts';

type SpinnerProps = {
  /** Verilirse `role="status"` ile duyurulur (ör. "Puantaj hesaplanıyor…"); yoksa dekoratiftir. */
  label?: string;
  size?: 16 | 20 | 24;
  className?: string;
};

/** Yükleniyor göstergesi (08 §6 #37). Hareket azaltma açıkken döndürme durur. */
export function Spinner({ label, size = 16, className }: SpinnerProps) {
  const icon = (
    <LoaderCircle aria-hidden size={size} strokeWidth={1.75} className={cn('animate-spin text-fg-muted', className)} />
  );
  if (!label) {
    return icon;
  }

  return (
    <span role="status" className="inline-flex items-center gap-2 text-body-sm text-fg-muted">
      {icon}
      <span>{label}</span>
    </span>
  );
}

/** İskelet: içerik yüklenirken yer tutar; ekran okuyucudan gizlidir. */
export function Skeleton({ className, ...props }: ComponentProps<'div'>) {
  return <div aria-hidden className={cn('animate-pulse rounded-md bg-surface-muted', className)} {...props} />;
}

type ProgressProps = {
  /** 0–100. */
  value: number;
  /** Erişilebilir ad; yoksa "İlerleme: yüzde N". */
  label?: string;
  className?: string;
};

export function Progress({ value, label, className }: ProgressProps) {
  const clamped = Math.min(100, Math.max(0, Math.round(value)));
  return (
    <ProgressPrimitive.Root
      value={clamped}
      aria-label={label ?? t('common.a11y.progress', { percent: clamped })}
      className={cn('relative h-2 w-full overflow-hidden rounded-full bg-surface-muted', className)}
    >
      <ProgressPrimitive.Indicator
        className="h-full bg-primary transition-transform duration-base"
        style={{ transform: `translateX(-${100 - clamped}%)` }}
      />
    </ProgressPrimitive.Root>
  );
}
